// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo } from 'react';

import type { BendTypeName } from 'helpers/helper-types';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { getBasicBendSpecifications } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';
import { useConduits } from 'modules/Shop/EvolveFabrication/useConduits';

import { useBendCreatorValidationContext } from './Context/useBendCreatorValidationContext';

export const useValidatedPreviewData = (previewBendType: BendTypeName) => {
  const { conduitSizes } = useConduits();
  const { lastValidation } = useBendCreatorValidationContext();

  return useMemo(() => {
    if (isNil(lastValidation) || !lastValidation.isSuccess) return null;
    const { response, request } = lastValidation;
    // Don't show the preview if the bend type has changed since the last validation.
    if (request.bendType !== previewBendType) return null;
    // We're given conduit size as an ID, but we need to know the measured size of the conduit.
    // If we can't find the conduit size, we can't generate the bend preview.
    const conduitSize = conduitSizes?.find(({ conduitSizeId }) => conduitSizeId === request.conduitSizeId)?.diameter;
    if (isNil(conduitSize)) return null;
    const { bendRadius, ybcData } = response;
    const baseBend = getBasicBendSpecifications({ bendRadius, conduitSize });
    response.updatedBend.forEach(({ keyName, value }) => {
      baseBend[keyName] = value;
    });
    if (isNotNil(ybcData)) {
      baseBend.ybcString = ybcData;
    }
    return baseBend;
  }, [lastValidation, previewBendType, conduitSizes]);
};
